import { z } from "zod";

import { BrazilAddressSchema } from "./address";
import { cnpjSchema } from "./br-tax-id";
import { CLIENT_RECORD_GROUPS } from "./common";
import { ContactSchema } from "./contact";

const BranchStatus = z.enum(["active", "archived"]);

const BranchFieldsSchema = z
  .object({
    name: z
      .string({ message: "branches_validation_name_required" })
      .min(1, { message: "branches_validation_name_required" })
      .max(200)
      .meta({ visibleTo: "*" }),
    taxId: cnpjSchema.optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    address: BrazilAddressSchema.optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    mainContact: ContactSchema.optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    additionalContacts: z
      .array(ContactSchema)
      .max(2)
      .default([])
      .meta({ visibleTo: CLIENT_RECORD_GROUPS }),
  })
  .describe("Branch (company client sub-office / filial) profile fields.");

export const CreateBranchSchema = BranchFieldsSchema.describe(
  "Payload to create a branch under a company client.",
);

export type CreateBranchInput = z.infer<typeof CreateBranchSchema>;

export const UpdateBranchSchema = BranchFieldsSchema.partial()
  .extend({ status: BranchStatus.optional() })
  .describe("Partial update for a branch.");

export type UpdateBranchInput = z.infer<typeof UpdateBranchSchema>;

export const BranchSchema = BranchFieldsSchema.extend({
  id: z.uuid().meta({ visibleTo: "*" }),
  status: BranchStatus.meta({ visibleTo: CLIENT_RECORD_GROUPS }),
  createdAt: z.iso.datetime().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
  updatedAt: z.iso.datetime().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
  createdBy: z.string().min(1).meta({ visibleTo: CLIENT_RECORD_GROUPS }),
}).describe("Branch embedded in a company client document.");

export type Branch = z.infer<typeof BranchSchema>;

export type LocatedBranch = Branch & { clientId: string };
export type BranchOption = Pick<Branch, "id" | "name">;

/**
 * Lenient branch shape for API responses where role-specific field sets may
 * omit required record fields — see `ClientApiResponseSchema` for the pattern.
 */
export const EmbeddedBranchApiResponseSchema = BranchSchema.partial()
  .extend({
    id: z.uuid().meta({ visibleTo: "*" }),
    name: z.string().meta({ visibleTo: "*" }),
    status: BranchStatus.optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
  })
  .describe("Possibly-masked branch document — see BranchSchema.");

export const BranchApiResponseSchema = EmbeddedBranchApiResponseSchema.extend({
  clientId: z.uuid().meta({ visibleTo: "*" }),
});

export type BranchApiResponse = z.infer<typeof BranchApiResponseSchema>;
